import { Inject, Injectable } from '@angular/core';
import SockJS from 'sockjs-client';
import { Environment, environmentToken } from '@environment';
import { CompatClient, Stomp } from '@stomp/stompjs';
import { Observable } from 'rxjs';
import type { IFrame, IMessage, StompSubscription } from '@stomp/stompjs';

interface JobListener {
  jobId: number;
  observer: { next: (packet: IMessage) => void };
  subscription?: StompSubscription;
  retryTimer?: ReturnType<typeof setTimeout>;
  retryAttempt: number;
  active: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OtapWebsocketService {
  private stompClient?: CompatClient;
  private connectionPromise?: Promise<CompatClient>;
  private readonly listeners = new Set<JobListener>();
  private disconnectRequested = false;

  constructor(@Inject(environmentToken) private readonly environment: Environment) {}

  public disconnect(): void {
    this.disconnectRequested = true;
    for (const listener of this.listeners) {
      this.stopListener(listener);
    }
    this.listeners.clear();

    const client = this.stompClient;
    this.stompClient = undefined;
    this.connectionPromise = undefined;
    if (client) {
      client.disconnect();
    }
  }

  public startConnection(): void {
    this.disconnectRequested = false;
    void this.ensureConnected().catch((error) => {
      console.error('Unable to establish the OTAP websocket connection.', error);
    });
  }

  public isConnected(): boolean {
    return Boolean(this.stompClient?.connected);
  }

  public listenToJob(jobId: number): Observable<IMessage> {
    return new Observable<IMessage>((observer) => {
      this.disconnectRequested = false;
      const listener: JobListener = {
        jobId,
        observer,
        retryAttempt: 0,
        active: true
      };

      this.listeners.add(listener);
      void this.subscribeListener(listener);

      return () => {
        this.stopListener(listener);
        this.listeners.delete(listener);
        if (this.listeners.size === 0) {
          this.disconnect();
        }
      };
    });
  }

  private ensureConnected(): Promise<CompatClient> {
    if (this.stompClient?.connected) {
      return Promise.resolve(this.stompClient);
    }
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    const client = Stomp.over(new SockJS(this.environment.apiUrl + '/ws/upgrade'));
    this.stompClient = client;
    this.connectionPromise = new Promise<CompatClient>((resolve, reject) => {
      const fail = (error: unknown): void => {
        if (this.stompClient === client) {
          this.stompClient = undefined;
          this.connectionPromise = undefined;
        }
        reject(error);
      };

      client.onWebSocketClose = (): void => {
        if (this.stompClient !== client || this.disconnectRequested) {
          return;
        }

        const wasConnected = client.connected;
        this.stompClient = undefined;
        this.connectionPromise = undefined;
        if (!wasConnected) {
          fail(new Error('OTAP websocket closed before STOMP connection completed.'));
        }
        for (const listener of this.listeners) {
          listener.subscription = undefined;
          this.scheduleListenerRetry(listener);
        }
      };
      client.onWebSocketError = (error: Event): void => {
        console.error('OTAP websocket transport error.', error);
      };
      client.onStompError = (frame: IFrame): void => {
        console.error('OTAP websocket STOMP error.', frame);
      };

      client.connect(
        {},
        () => {
          this.connectionPromise = undefined;
          resolve(client);
        },
        fail
      );
    });

    return this.connectionPromise;
  }

  private async subscribeListener(listener: JobListener): Promise<void> {
    if (!listener.active) {
      return;
    }

    try {
      const client = await this.ensureConnected();
      if (!listener.active || !client.connected) {
        return;
      }

      listener.subscription = client.subscribe(`/topic/job/${listener.jobId}`, (packet) => {
        if (listener.active) {
          listener.observer.next(packet);
        }
      });
      listener.retryAttempt = 0;
    } catch (error) {
      if (!listener.active) {
        return;
      }

      console.error(`Unable to subscribe to OTAP job ${listener.jobId}.`, error);
      this.scheduleListenerRetry(listener);
    }
  }

  private scheduleListenerRetry(listener: JobListener): void {
    if (!listener.active || listener.retryTimer) {
      return;
    }

    const delay = Math.min(1000 * 2 ** listener.retryAttempt, 10000);
    listener.retryAttempt += 1;
    listener.retryTimer = setTimeout(() => {
      listener.retryTimer = undefined;
      void this.subscribeListener(listener);
    }, delay);
  }

  private stopListener(listener: JobListener): void {
    listener.active = false;
    if (listener.retryTimer) {
      clearTimeout(listener.retryTimer);
      listener.retryTimer = undefined;
    }
    listener.subscription?.unsubscribe();
    listener.subscription = undefined;
  }
}
